// src/hooks/useOccupancy.js
import { useEffect, useState, useRef } from 'react';

// Fallback poll cadence — only used if the live stream can't stay connected
// for a while. Otherwise updates arrive over the stream as TB emits them,
// typically within ~1s of a real occupancy change.
const FALLBACK_POLL_INTERVAL_MS = 15000;

// Watchdog window: if no activity (open or message) arrives within this long,
// fall back to polling. Covers three cases uniformly: the stream never opens,
// it opens but device resolution never completes server-side, or it opens and
// then silently goes quiet without ever firing onerror. Set comfortably above
// the mmWave firmware's own ~30s heartbeat cadence so a healthy-but-quiet
// device doesn't false-trigger a fallback.
const SSE_FALLBACK_AFTER_MS = 60000;
const WATCHDOG_CHECK_INTERVAL_MS = 10000;

// Module-level cache survives React StrictMode remounts so the strip doesn't
// flash empty on a quick remount.
const occupancyCache = new Map();

/**
 * Occupancy hook — subscribes to the server's /api/occupancy/stream SSE feed,
 * which is backed by a persistent server-side ThingsBoard WebSocket
 * subscription (server/occupancyWs.js). No TB credentials reach the browser;
 * the server authenticates to TB independently with its own service account.
 * Falls back to polling /api/occupancy if the stream can't stay connected.
 *
 * `tbToken` is accepted for call-signature compatibility with existing
 * callers but is no longer used — the stream needs no client-side TB token.
 *
 * Returns { [washroomId]: { occupied: boolean, lastUpdated: string } }
 */
export default function useOccupancy(washrooms, tbToken, apiBase) {
  const [data, setData] = useState(() => {
    const cached = {};
    washrooms?.forEach((wr) => {
      const entry = occupancyCache.get(wr.id);
      if (entry) cached[wr.id] = entry;
    });
    return cached;
  });

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!washrooms?.length || apiBase == null) {
      return () => {
        mountedRef.current = false;
      };
    }

    const assetIds = washrooms.map((wr) => wr.id);

    const updateOccupancy = (assetId, occupied, lastUpdated) => {
      const entry = {
        occupied,
        lastUpdated: lastUpdated || new Date().toISOString(),
      };
      occupancyCache.set(assetId, entry);
      if (mountedRef.current) {
        setData((prev) => ({ ...prev, [assetId]: entry }));
      }
    };

    let closed = false;
    let eventSource = null;
    let pollTimer = null;
    let pollInFlight = false;
    let watchdogTimer = null;
    let lastActivityMs = Date.now();

    const pollOnce = async () => {
      if (pollInFlight) return;
      pollInFlight = true;
      try {
        for (const assetId of assetIds) {
          if (closed) return;
          try {
            const url = `${apiBase}/api/occupancy?assetId=${encodeURIComponent(assetId)}`;
            const response = await fetch(url);
            if (!response.ok) continue;
            const result = await response.json();
            if (result.ok && result.data?.exists) {
              updateOccupancy(assetId, result.data.occupied, result.data.lastUpdated);
            }
          } catch {
            /* transient fetch error — the next poll retries */
          }
        }
      } finally {
        pollInFlight = false;
      }
    };

    const startPolling = () => {
      if (pollTimer) return;
      pollOnce();
      pollTimer = setInterval(pollOnce, FALLBACK_POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const markActivity = () => {
      lastActivityMs = Date.now();
      stopPolling();
    };

    const connectStream = () => {
      const streamUrl = `${apiBase}/api/occupancy/stream?assetIds=${assetIds.map(encodeURIComponent).join(',')}`;
      eventSource = new EventSource(streamUrl);

      eventSource.onopen = markActivity;

      eventSource.onmessage = (event) => {
        markActivity();
        try {
          const { assetId, occupied, lastUpdated } = JSON.parse(event.data);
          if (assetId) updateOccupancy(assetId, occupied, lastUpdated);
        } catch {
          /* malformed event — the next one will land */
        }
      };

      // No explicit fallback here — the browser retries the connection on
      // its own, and the watchdog below (driven by lastActivityMs) catches
      // both this case and the case where the connection stays technically
      // open but silently never delivers anything.
      eventSource.onerror = () => {};
    };

    connectStream();

    // Single source of truth for "is the stream actually alive": if neither
    // an open nor a message has landed recently, fall back to polling. Runs
    // regardless of whether onerror ever fires, since a stalled-but-open
    // connection wouldn't trigger it.
    watchdogTimer = setInterval(() => {
      if (closed) return;
      if (Date.now() - lastActivityMs > SSE_FALLBACK_AFTER_MS) startPolling();
    }, WATCHDOG_CHECK_INTERVAL_MS);

    return () => {
      closed = true;
      mountedRef.current = false;
      if (eventSource) eventSource.close();
      if (watchdogTimer) clearInterval(watchdogTimer);
      stopPolling();
    };
    // tbToken intentionally excluded — the stream needs no client token, so
    // a token swap shouldn't restart the connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washrooms, apiBase]);

  return data;
}
